import { Controller } from "@hotwired/stimulus"

// Debounced live search controller for organization autocomplete
// Shows a dropdown of matching organizations as user types
export default class extends Controller {
  static targets = ["input", "dropdown", "results"]
  static values = {
    url: String,
    minLength: { type: Number, default: 2 },
    debounce: { type: Number, default: 300 }
  }

  connect() {
    this.timeout = null
    this.selectedIndex = -1

    // Close dropdown when clicking outside
    this.boundCloseDropdown = this.closeOnClickOutside.bind(this)
    document.addEventListener('click', this.boundCloseDropdown)
  }

  disconnect() {
    if (this.timeout) clearTimeout(this.timeout)
    document.removeEventListener('click', this.boundCloseDropdown)
  }

  // Debounced search triggered on input
  search(event) {
    const query = event.target.value.trim()

    if (this.timeout) clearTimeout(this.timeout)

    if (query.length < this.minLengthValue) {
      this.hideDropdown()
      return
    }

    // Show loading indicator immediately
    this.showLoading()

    this.timeout = setTimeout(() => {
      this.fetchResults(query)
    }, this.debounceValue)
  }

  showLoading() {
    this.resultsTarget.innerHTML = `
      <div class="dropdown-item text-muted py-3 text-center">
        <i class="fa fa-spinner fa-spin me-2"></i>
        Searching...
      </div>
    `
    this.showDropdown()
  }

  async fetchResults(query) {
    try {
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set('q', query)

      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) throw new Error('Search failed')

      const results = await response.json()
      this.renderResults(results)
    } catch (error) {
      console.error('Live search error:', error)
      this.hideDropdown()
    }
  }

  renderResults(results) {
    if (results.length === 0) {
      this.resultsTarget.innerHTML = `
        <div class="dropdown-item text-muted py-2">
          No organizations found
        </div>
      `
      this.showDropdown()
      return
    }

    const brandColor = getComputedStyle(document.documentElement).getPropertyValue('--gd-lead-color').trim() || '#722432'

    const html = results.map((org, index) => `
      <a href="/organizations/${org.slug}"
         class="dropdown-item org-search-result d-flex align-items-center py-2 px-3"
         data-index="${index}"
         data-brand-color="${brandColor}"
         onmouseenter="this.style.backgroundColor='${brandColor}'; this.style.color='#fff'; this.querySelectorAll('.org-name, .org-location, i').forEach(el => el.style.color='#fff')"
         onmouseleave="this.style.backgroundColor=''; this.style.color=''; this.querySelector('.org-name').style.color='#333'; var loc = this.querySelector('.org-location'); if(loc) loc.style.color='#6c757d'; this.querySelector('.fa-chevron-right').style.color='#6c757d'">
        <div class="org-logo-wrapper me-3">
          ${org.logo_url
            ? `<img src="${org.logo_url}" alt="${org.name}" class="org-logo-thumb rounded" style="width: 40px; height: 40px; object-fit: cover;">`
            : `<div class="org-logo-placeholder rounded d-flex align-items-center justify-content-center" style="width: 40px; height: 40px; background: #e9ecef;">
                 <i class="fa fa-building" style="color: #6c757d;"></i>
               </div>`
          }
        </div>
        <div class="org-info flex-grow-1">
          <div class="org-name fw-semibold" style="color: #333;">${this.escapeHtml(org.name)}</div>
          ${org.location ? `<small class="org-location" style="color: #6c757d;">${this.escapeHtml(org.location)}</small>` : ''}
        </div>
        <i class="fa fa-chevron-right ms-2" style="color: #6c757d;"></i>
      </a>
    `).join('')

    this.resultsTarget.innerHTML = html
    this.selectedIndex = -1
    this.showDropdown()
  }

  showDropdown() {
    this.dropdownTarget.classList.add('show')
  }

  hideDropdown() {
    this.dropdownTarget.classList.remove('show')
    this.selectedIndex = -1
  }

  closeOnClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideDropdown()
    }
  }

  // Handle keyboard navigation
  keydown(event) {
    const results = this.resultsTarget.querySelectorAll('.org-search-result')

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.selectedIndex = Math.min(this.selectedIndex + 1, results.length - 1)
        this.updateHighlight(results)
        break
      case 'ArrowUp':
        event.preventDefault()
        this.selectedIndex = Math.max(this.selectedIndex - 1, -1)
        this.updateHighlight(results)
        break
      case 'Enter':
        if (this.selectedIndex >= 0 && results[this.selectedIndex]) {
          event.preventDefault()
          results[this.selectedIndex].click()
        }
        // Otherwise let the form submit normally
        break
      case 'Escape':
        this.hideDropdown()
        break
    }
  }

  updateHighlight(results) {
    results.forEach((el, i) => {
      el.classList.toggle('active', i === this.selectedIndex)
    })
  }

  highlightResult(event) {
    const index = parseInt(event.target.closest('.org-search-result')?.dataset?.index || -1)
    if (index >= 0) {
      this.selectedIndex = index
      const results = this.resultsTarget.querySelectorAll('.org-search-result')
      this.updateHighlight(results)
    }
  }

  // Clear search and hide dropdown on focus out (with delay for click handling)
  blur() {
    setTimeout(() => {
      this.hideDropdown()
    }, 200)
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
